// 运行日志与 SSE 事件流 / Run-log + SSE event stream.
// 端口自 legacy app.js 的 connectEvents / appendLog / clearLog / handleSimulationEvent
// 等片段；绑定 Stage B 后端真实存在的 7 个事件类型（D4 + run.timeout 回放补齐）。
// T11 之后：日志行经由 terminal-buffer 进入右侧日志终端；state 合并语义保持不变。
// Binds all 7 real Stage B SSE events; log lines now flow into terminal-buffer,
// while state-merge side effects (activeSimulation / currentTask / lastReportId /
// artifacts / running) remain unchanged.

import { SseClient } from "@/lib/sse";
import { state } from "@/state/store";
import type { ReportStatus, SimulationRow } from "@/lib/types";
import { buildApiUrl } from "@/api/client";
import {
  appendEntry,
  clearTab,
} from "@/features/log-terminal/terminal-buffer";

// Stage B SSE 事件 payload（任意字段，后端由 SimulationRow + run + report_id 构成）
// Stage B SSE event payload shape.
interface SimulationEventPayload {
  type?: string;
  run?: string;
  arm?: string;
  status?: string;
  message?: string;
  error?: string;
  simulation?: SimulationRow;
  report_id?: string;
  report_status?: ReportStatus;
  job_id?: string;
  agentcore_job_id?: string;
}

// 激活的 SSE 客户端 / Currently-active SSE client (module-local singleton).
let activeClient: SseClient<SimulationEventPayload> | null = null;

// 可选回调：SSE 到达终态时触发（由 run-control 注入以清理 UI）
// Optional callback fired when the stream reaches a terminal state.
let onTerminalCallback: ((payload: SimulationEventPayload) => void) | null = null;

/**
 * 订阅某个仿真的 SSE 事件流。
 * Subscribe to the SSE event stream for a simulation.
 * @param simId 仿真 id / simulation id
 * @param onTerminal 终态回调 / terminal-state callback
 */
export function connectEvents(
  simId: string,
  onTerminal?: (payload: SimulationEventPayload) => void,
): void {
  if (activeClient) {
    activeClient.close();
    activeClient = null;
  }
  onTerminalCallback = onTerminal ?? null;

  const handlers: Record<string, (e: SimulationEventPayload) => void> = {
    snapshot: handleSnapshot,
    "run.starting": (e) => handleEvent("run.starting", e),
    "run.created": (e) => handleEvent("run.created", e),
    "run.completed": (e) => handleEvent("run.completed", e, true),
    "run.failed": (e) => handleEvent("run.failed", e, true),
    "run.cancelled": (e) => handleEvent("run.cancelled", e, true),
    // run.timeout 与 run.failed 共享 terminal-error 形状（AgentCore 超时回放）
    // run.timeout mirrors run.failed as a terminal-error (AgentCore timeout replay).
    "run.timeout": (e) => handleEvent("run.timeout", e, true),
    "report.started": (e) => handleEvent("report.started", e),
    "report.failed": (e) => handleEvent("report.failed", e),
    // regenerate 完成 → 视为终态；让 task-detail 监听 onTerminal 拿到新 report_id /
    // Regenerate completion is treated as terminal so task-detail sees the new report_id.
    "report.regenerated": (e) => handleEvent("report.regenerated", e, true),
    // NOTE: run.progress 不存在于当前 Stage B 实现中（D4）
    // Intentionally no run.progress handler — no such event in Stage B.
  };
  activeClient = new SseClient<SimulationEventPayload>(
    buildApiUrl(`/api/v1/simulations/${encodeURIComponent(simId)}/events`),
    handlers,
  );
  activeClient.connect();
}

/**
 * 关闭当前 SSE / Close the active SSE client.
 */
export function closeEvents(): void {
  activeClient?.close();
  activeClient = null;
}

/**
 * 追加一行日志到 log terminal / Append one line to the log terminal buffer.
 * 无 run 字段时落到 system tab；带 run 时按 A/B 归一化到 planA / planB。
 * Without `run`, routes to the system tab; otherwise normalized to planA / planB.
 */
export function appendLog(line: string, run?: string): void {
  if (!run) {
    appendEntry({
      tab: "system",
      timestamp: Date.now(),
      severity: "info",
      density: "compact",
      text: line,
      rawKey: `log-stream:system:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
    });
    return;
  }
  const slot = normalizeRunSlot(run);
  appendLogToSlot(slot, line);
}

/**
 * 清空某一侧的 log / Clear a specific slot's log.
 * 映射：A → planA, B → planB。Maps to the terminal buffer's plan tab.
 */
export function clearLog(slot: "A" | "B"): void {
  clearTab(slot === "B" ? "planB" : "planA");
}

// 处理 snapshot 事件：写入 state.activeSimulation / Handle snapshot event: persist active simulation row.
function handleSnapshot(payload: SimulationEventPayload): void {
  mergeSimulationPayload(payload, false);
}

// 通用事件处理：日志 + 可选终态 / Generic event handler: log + optional terminal close.
function handleEvent(
  type: string,
  payload: SimulationEventPayload,
  terminal = false,
): void {
  appendLogWithKey(formatEvent(type, payload), payload.run, stableLogKey(type, payload));
  syncReportGenerationState(type, payload, terminal);
  mergeSimulationPayload(payload, terminal);
  if (terminal) {
    state.running = false;
    activeClient?.close();
    activeClient = null;
    if (onTerminalCallback) {
      const cb = onTerminalCallback;
      onTerminalCallback = null;
      cb(payload);
    }
  }
}

function syncReportGenerationState(
  type: string,
  payload: SimulationEventPayload,
  terminal: boolean,
): void {
  const simulationId = payload.simulation?.id ?? null;
  if (type === "report.started" && simulationId) {
    state.reportGeneration = {
      simulationId,
      taskId: payload.simulation?.task_id ?? null,
      jobId: payload.job_id ?? payload.agentcore_job_id ?? null,
      status: "pending",
    };
    return;
  }
  if (terminal && simulationId && state.reportGeneration?.simulationId === simulationId) {
    state.reportGeneration = null;
  }
}

// 拼出单行日志；时间列由 terminal renderer 统一负责。
// Compose one log line; the terminal renderer owns timestamp display.
function formatEvent(type: string, payload: SimulationEventPayload): string {
  if (type === "report.started") return "⟳ Generating report";
  if (type === "report.regenerated") return "✓ Report generated";
  if (type === "report.failed") return `✗ Report generation failed: ${friendlyReportError(payload.error)}`.trim();
  const runPrefix = payload.run || payload.arm ? `[${payload.run ?? payload.arm}] ` : "";
  const message =
    payload.message ||
    payload.status ||
    payload.error ||
    (payload.agentcore_job_id ? `agentcore=${payload.agentcore_job_id}` : "");
  return `${type} ${runPrefix}${message}`.trim();
}

function appendLogWithKey(line: string, run: string | undefined, rawKey: string | null): void {
  if (run || !rawKey) {
    appendLog(line, run);
    return;
  }
  appendEntry({
    tab: "system",
    timestamp: Date.now(),
    severity: line.startsWith("✗") ? "warn" : "info",
    density: "compact",
    text: line,
    rawKey,
  });
}

function stableLogKey(type: string, payload: SimulationEventPayload): string | null {
  if (type !== "report.started") return null;
  const stableId =
    payload.job_id ||
    payload.agentcore_job_id ||
    payload.simulation?.id ||
    payload.report_status ||
    "pending";
  return `log-stream:report.started:${stableId}`;
}

function friendlyReportError(error: string | null | undefined): string {
  const message = String(error ?? "").trim();
  const lowered = message.toLowerCase();
  if (!message) return "Please try again later.";
  if (
    lowered.includes("runtimeclienterror") ||
    lowered.includes("starting the runtime") ||
    lowered.includes("cloudwatch") ||
    lowered.includes("job_start_failed")
  ) {
    return "The report generation service is temporarily unavailable. Please try again later; if deployment just finished, confirm AgentCore has been redeployed and is healthy.";
  }
  if (lowered.includes("unsupported_job_type") || lowered.includes("unsupported job type")) {
    return "The current AgentCore version does not support report generation. Redeploy AgentCore and try again.";
  }
  if (lowered.includes("markdown_na_leak") || lowered.includes("markdown_waiting_placeholder")) {
    return "The report still contains unresolved placeholders, so publishing was blocked. Please regenerate the report.";
  }
  return message;
}

// 归一化 run 标识为 "A" / "B" / Normalize a run label to A or B.
function normalizeRunSlot(run: string): "A" | "B" {
  const v = String(run ?? "").trim().toUpperCase();
  // Stage B 使用 "plan_a" / "plan_b" 或 "A" / "B"；取最末尾字符判断
  // Accept "plan_a"/"plan_b" or "A"/"B"; use the last non-empty char to decide.
  return v.endsWith("B") ? "B" : "A";
}

// 写入单个 slot 的 log buffer / Append text into the corresponding plan tab buffer.
function appendLogToSlot(slot: "A" | "B", line: string): void {
  appendEntry({
    tab: slot === "B" ? "planB" : "planA",
    timestamp: Date.now(),
    severity: "info",
    density: "compact",
    text: line,
    rawKey: `log-stream:${slot}:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`,
  });
}

// 合并 SSE 仿真状态 / Merge simulation state carried by SSE payloads.
function mergeSimulationPayload(payload: SimulationEventPayload, terminal: boolean): void {
  const simulationId = payload.simulation?.id ?? null;
  const ownsCurrentTask =
    state.currentTask?.kind === "history" &&
    Boolean(simulationId) &&
    state.currentTask.simulationId === simulationId;
  if (payload.simulation) {
    state.activeSimulation = payload.simulation;
    if (ownsCurrentTask && payload.simulation.artifacts) {
      state.artifacts = payload.simulation.artifacts;
    }
  }

  const reportId = payload.report_id ?? payload.simulation?.report_id ?? null;
  const reportStatus = resolveReportStatus(
    reportId,
    payload.report_status ?? payload.simulation?.report_status ?? null,
    terminal ? payload.simulation?.status ?? null : null,
  );
  if (ownsCurrentTask && reportStatus === "ready") {
    state.lastReportId = reportId;
  }

  if (terminal && ownsCurrentTask && simulationId) {
    state.currentTask = {
      kind: "history",
      simulationId,
      reportId: reportStatus === "ready" ? reportId : null,
      reportStatus,
    };
    state.lastReportId = reportStatus === "ready" ? reportId : null;
  }
}

// 规范化报告状态 / Normalize report status.
function resolveReportStatus(
  reportId: string | null,
  reportStatus: ReportStatus | null,
  simulationStatus: string | null,
): ReportStatus {
  if (reportId) return "ready";
  if (reportStatus) return reportStatus;
  if (
    simulationStatus === "failed" ||
    simulationStatus === "cancelled" ||
    simulationStatus === "timeout"
  ) {
    return "failed";
  }
  return "pending";
}
